import { ascending } from 'd3-array'

import { formatValue } from 'nswidgets/src/components/line/format.service'
import { listService } from '../../../common/services/list.service'

class LineBlockService {
  /**
   * Filter the raw data based on the query and map into the data structure required by the line chart
   *
   * @param {object[]} data Array of objects containing the raw field data
   * @param {object} dataDefinition Field names to use for x, y, and y2 values
   * @param {string} stratifyBy Field to stratify by
   * @param {object} strataDefinition Stratification definition
   */
  transformData (data, dataDefinition, stratifyBy, strataDefinition) {
    const { xField, yField, y2Field, yLoField, yHiField } = dataDefinition

    const series = data.reduce((series, r) => {
      // Find series for this data point and add if doesn't exist
      const seriesId = `${stratifyBy}_${r[stratifyBy]}`
      const name = r[stratifyBy] || 'Overall'
      let s = series.find(({ id }) => id === seriesId)
      if (!s) {
        s = {
          id: seriesId,
          name,
          points: []
        }
        series.push(s)
      }

      // Set point values
      let point = {
        x: r[xField],
        y: r[yField]
      }
      if (y2Field) {
        point.y2 = r[y2Field]
      }
      if (yLoField) {
        point.yLo = r[yLoField]
      }
      if (yHiField) {
        point.yHi = r[yHiField]
      }

      s.points.push(point)
      return series
    }, [])

    // Sort points
    series.forEach((thisSeries) => {
      thisSeries.points.sort((point1, point2) => (ascending(point1.x, point2.x)))
    })

    if (strataDefinition && Array.isArray(strataDefinition.options)) {
      const currentStrata = strataDefinition.options.find((option) => (option.value === stratifyBy))
      if (currentStrata && Array.isArray(currentStrata.legend)) {
        // Order series (thereby the legend) based on stratification definition configuration
        const order = currentStrata.legend.map(({ value }) => (value))
        listService.sortObjectList(series, 'name', order)

        // Apply labels to legend (renames the legend)
        series.forEach((thisSeries) => {
          const thisLegend = currentStrata.legend.find(({ value }) => (value === thisSeries.name))
          if (thisLegend && thisLegend.label) {
            thisSeries.label = thisLegend.label
          }
        })
      }
    }

    return series
  }

  transformToTableData (data, dataDefinition = {}, selections = {}, strataDefinition, { isTimeSeries, xFormat, timeFormat, columnsToHide = {} }) {
    const rowField = dataDefinition.xField
    const columnField = selections.strata
    let legendLabelMap = {}

    if (strataDefinition && Array.isArray(strataDefinition.options)) {
      const currentStrata = strataDefinition.options.find((option) => (option.value === columnField))
      if (currentStrata && Array.isArray(currentStrata.legend)) {
        legendLabelMap = currentStrata.legend.reduce((result, legend) => {
          result[legend.value] = legend.label
          return result
        }, {})
      }
    }

    return data
      .map((d) => {
        const column = d[columnField]
        const columnName = column ? (legendLabelMap[column] || column) : column
        const dataPoint = {
          row: this.formatXValueForTable(d[rowField], { isTimeSeries, xFormat, timeFormat }),
          column: (columnField === null || columnField === dataDefinition.emptyValue) ? 'Overall' : columnName,
          ...d
        }
        return dataPoint
      })
      .filter((d) => (!columnsToHide[d.column]))
  }

  formatXValueForTable (value, { isTimeSeries, xFormat, timeFormat }) {
    xFormat = isTimeSeries ? 'date' : xFormat
    return formatValue(value, { formatType: xFormat, timeFormat })
  }
}

export const lineBlockService = new LineBlockService()
